<template>
  <div>
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-2.5">
        <h1 class="text-2xl font-bold tracking-tight text-ink m-0">Applicants</h1>
        <span class="bg-[#f0eef5] text-primary text-[13px] font-semibold px-2.5 py-0.5 rounded-full">{{ activeApplicants.length }}</span>
      </div>
      <a v-if="user && user.isAdmin" href="#" @click.prevent="openEditModal(null)"
        class="bg-ink text-white text-sm font-medium px-[18px] py-2 rounded-full no-underline transition-colors duration-150 hover:bg-[#2a2b2d] hover:text-white hover:no-underline">
        + Add applicant
      </a>
    </div>

    <!-- Toolbar: tabs + search -->
    <div class="flex items-center justify-between gap-4 mb-5 max-md:flex-col max-md:items-stretch">
      <div class="flex gap-1 bg-[#f4f5fa] p-1 rounded-[14px]" role="tablist">
        <button v-for="tab in visibleTabs" :key="tab.key"
          role="tab"
          :aria-selected="activeTab === tab.key"
          class="tab-btn bg-transparent border-none px-4 py-2 text-sm font-medium text-muted rounded-[10px] cursor-pointer transition-all duration-150 flex items-center gap-2 hover:text-ink"
          :class="{
            'bg-white text-ink shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]': activeTab === tab.key
          }"
          @click="activeTab = tab.key">
          {{ tab.label }}
          <span class="tab-count text-[11px] font-semibold text-placeholder min-w-[18px] text-center">{{ getTabCount(tab.key) }}</span>
        </button>
      </div>

      <div class="flex items-center gap-2 max-md:flex-col">
        <base-select
          v-model="selectedBody"
          :options="bodies"
          value-key="slug"
          label-key="title"
          placeholder="All bodies"
          class="min-w-[150px] max-md:w-full"
        ></base-select>
        <div class="search-box flex items-center gap-2 bg-[#fafafa] border border-border rounded-xl px-3.5 h-10 min-w-[200px] transition-[border-color,background] duration-150 text-placeholder max-md:min-w-0 max-md:w-full">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          <input
            type="text"
            ref="searchInput"
            v-model="searchQuery"
            placeholder="Search by name or body..."
            class="search-input border-none outline-none bg-transparent text-[13px] text-ink w-full h-full placeholder:text-placeholder">
          <kbd class="hidden md:inline-flex items-center gap-0.5 text-[11px] text-placeholder font-medium bg-white border border-border rounded px-1.5 py-0.5 leading-none shrink-0 pointer-events-none">&#8984;K</kbd>
        </div>
      </div>
    </div>

    <!-- Quick stats -->
    <div v-if="!isLoading && quickStats.length > 0" class="flex items-center gap-2 mb-4 flex-wrap">
      <span v-for="stat in quickStats" :key="stat.label"
        class="text-xs font-medium px-3 py-1 rounded-full"
        :class="{
          'bg-[#ffebee] text-[#e53935]': stat.type === 'danger',
          'bg-[#fff3e0] text-[#f57c00]': stat.type === 'warning',
          'bg-[#e8f5e9] text-[#2d7a0e]': stat.type === 'success',
          'bg-[#f0eef5] text-muted': stat.type === 'neutral'
        }">
        {{ stat.count }} {{ stat.label }}
      </span>
    </div>

    <skeleton-applicant-list v-if="isLoading"></skeleton-applicant-list>

    <!-- Empty state -->
    <div v-if="!isLoading && searchedApplicants && searchedApplicants.length === 0" class="flex flex-col items-center py-[60px] px-5 text-placeholder">
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
      <p class="text-sm text-muted mt-4 mb-0" v-if="searchQuery">No applicants matching "{{ searchQuery }}"</p>
      <p class="text-sm text-muted mt-4 mb-0" v-else-if="activeTab === 'inactive'">No archived applicants</p>
      <p class="text-sm text-muted mt-4 mb-0" v-else>No applicants found</p>
      <a v-if="!searchQuery" href="#" @click.prevent="openEditModal(null)" class="text-sm font-semibold text-primary no-underline mt-2 hover:text-[#6347c5] hover:no-underline">+ Add your first applicant</a>
    </div>

    <!-- Column headers -->
    <div v-if="!isLoading && searchedApplicants && searchedApplicants.length > 0" class="flex items-center gap-4 px-5 mb-1 max-md:hidden sticky top-0 z-10 bg-canvas/80 backdrop-blur-sm py-2 -mt-2">
      <div class="text-[11px] font-semibold uppercase tracking-wide text-muted whitespace-nowrap w-10 shrink-0"></div>
      <button class="sort-header text-[11px] font-semibold uppercase tracking-wide text-muted whitespace-nowrap flex-1 min-w-0 cursor-pointer select-none flex items-center gap-1 hover:text-ink transition-colors duration-150 bg-transparent border-0 p-0 text-left" @click="toggleSort('name')">
        Name
        <svg class="sort-chevron shrink-0 transition-all duration-150" :class="[sortField === 'name' ? 'text-primary opacity-100' : 'opacity-0', sortField === 'name' && sortDirection === 'desc' ? 'rotate-180' : '']" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
      </button>
      <button class="sort-header text-[11px] font-semibold uppercase tracking-wide text-muted whitespace-nowrap w-[70px] shrink-0 cursor-pointer select-none flex items-center gap-1 hover:text-ink transition-colors duration-150 max-lg:hidden bg-transparent border-0 p-0 text-left" @click="toggleSort('type')">
        Type
        <svg class="sort-chevron shrink-0 transition-all duration-150" :class="[sortField === 'type' ? 'text-primary opacity-100' : 'opacity-0', sortField === 'type' && sortDirection === 'desc' ? 'rotate-180' : '']" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
      </button>
      <button class="sort-header text-[11px] font-semibold uppercase tracking-wide text-muted whitespace-nowrap w-[140px] shrink-0 cursor-pointer select-none flex items-center gap-1 hover:text-ink transition-colors duration-150 max-lg:w-[100px] bg-transparent border-0 p-0 text-left" @click="toggleSort('progress')">
        Progress
        <svg class="sort-chevron shrink-0 transition-all duration-150" :class="[sortField === 'progress' ? 'text-primary opacity-100' : 'opacity-0', sortField === 'progress' && sortDirection === 'desc' ? 'rotate-180' : '']" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
      </button>
      <button class="sort-header text-[11px] font-semibold uppercase tracking-wide text-muted whitespace-nowrap w-[100px] shrink-0 cursor-pointer select-none flex items-center gap-1 hover:text-ink transition-colors duration-150 bg-transparent border-0 p-0 text-left" @click="toggleSort('deadline')">
        Deadline
        <svg class="sort-chevron shrink-0 transition-all duration-150" :class="[sortField === 'deadline' ? 'text-primary opacity-100' : 'opacity-0', sortField === 'deadline' && sortDirection === 'desc' ? 'rotate-180' : '']" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
      </button>
      <div class="text-[11px] font-semibold uppercase tracking-wide text-muted whitespace-nowrap w-20 shrink-0 max-lg:hidden">Team</div>
      <div class="text-[11px] font-semibold uppercase tracking-wide text-muted whitespace-nowrap w-[100px] shrink-0"></div>
    </div>

    <!-- List -->
    <div v-if="applicants && applicants.length > 0" class="flex flex-col gap-1">
      <router-link
        v-for="applicant in searchedApplicants"
        :key="applicant.id"
        :to="{ name: 'admin-applicant', params: { id: applicant.id }}"
        class="group flex items-center gap-4 bg-white border border-border rounded-[16px] px-5 py-3.5 no-underline text-ink transition-[border-color,box-shadow] duration-150 hover:border-border-hover hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] hover:no-underline hover:text-ink max-md:flex-wrap max-md:px-4 max-md:py-3 max-md:gap-x-3 max-md:gap-y-2"
        :class="{
          'opacity-40': !applicant.isActive,
          'bg-[#f8fbf4] border-[#e2f3d1]': getCompletedPercentage(applicant) === 100
        }">

        <!-- Col 1: Avatar -->
        <div class="relative shrink-0 w-10 h-10 max-md:w-9 max-md:h-9">
          <img v-if="applicant.picture" :src="'/static/images/clients/' + applicant.picture" :alt="applicantName(applicant)" class="w-10 h-10 rounded-full object-cover block max-md:w-9 max-md:h-9">
          <div v-else class="w-10 h-10 rounded-full bg-[#f0eef5] text-primary flex items-center justify-center font-semibold text-[15px] max-md:w-9 max-md:h-9 max-md:text-sm">{{ applicant.firstName ? applicant.firstName.charAt(0) : '' }}</div>
          <span v-if="getCompletedPercentage(applicant) === 100" class="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#2d7a0e] border-2 border-white flex items-center justify-center text-white">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
          </span>
        </div>

        <!-- Col 2: Name + body -->
        <div class="flex-1 min-w-0 flex flex-col gap-px">
          <span class="text-[15px] font-semibold text-ink leading-tight whitespace-nowrap overflow-hidden text-ellipsis group-hover:text-primary">{{ applicantName(applicant) }}</span>
          <span class="text-[13px] font-medium text-muted whitespace-nowrap overflow-hidden text-ellipsis">{{ getEndorsingBody(applicant.endorsingBody) }}</span>
        </div>

        <!-- Col 3: Type -->
        <div class="w-[70px] shrink-0 max-lg:hidden">
          <span v-if="applicant.type" class="text-[11px] font-semibold px-2 py-[3px] rounded uppercase tracking-wide whitespace-nowrap"
            :class="{
              'bg-[#f0eef5] text-primary': applicant.type === 'talent',
              'bg-[#e8f4fb] text-[#3AA6DD]': applicant.type === 'promise'
            }">
            {{ applicant.type === 'talent' ? 'Talent' : 'Promise' }}
          </span>
          <span v-else class="text-placeholder text-[13px]">&mdash;</span>
        </div>

        <!-- Col 4: Progress -->
        <div class="flex items-center gap-2 w-[140px] shrink-0 max-lg:w-[100px] max-md:w-full max-md:order-[10]">
          <div class="flex-1 h-1.5 rounded-[3px] overflow-hidden"
            :class="getCompletedPercentage(applicant) === 100 ? 'bg-[#d4e8c4]' : 'bg-border'">
            <div class="h-full rounded-[3px] transition-[width] duration-300 ease-in-out"
              :class="getCompletedPercentage(applicant) === 100 ? 'bg-[#2d7a0e]' : 'bg-primary'"
              :style="{ width: getCompletedPercentage(applicant) + '%' }"></div>
          </div>
          <span class="text-xs font-semibold whitespace-nowrap tabular-nums min-w-[28px]"
            :class="getCompletedPercentage(applicant) === 100 ? 'text-[#2d7a0e]' : 'text-muted'">{{ getCompletedCount(applicant) }}/{{ applicant.requirements ? applicant.requirements.length : 0 }}</span>
        </div>

        <!-- Col 5: Deadline -->
        <div class="flex items-center gap-1 text-[13px] font-medium whitespace-nowrap w-[100px] shrink-0 max-md:flex-1 max-md:order-[20]"
          :class="{
            'text-[#e53935]': getDeadlineUrgency(applicant) === 'danger',
            'text-[#f57c00]': getDeadlineUrgency(applicant) === 'warning',
            'text-[#2d7a0e]': getDeadlineUrgency(applicant) === 'safe',
            'text-muted': !getDeadlineUrgency(applicant)
          }">
          <template v-if="applicant.deadline">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
            {{ applicant.deadline | formatDate }}
          </template>
          <span v-else class="text-placeholder text-xs">No deadline</span>
        </div>

        <!-- Col 6: People -->
        <div class="flex shrink-0 w-20 max-lg:hidden">
          <template v-if="getPhotos(applicant).length > 0">
            <template v-for="person in getPhotos(applicant)">
              <img v-if="getPersonPhoto(person)" :key="person" class="w-8 h-8 rounded-full border-2 border-white object-cover block -ml-1.5 first:ml-0" :src="getPersonPhoto(person)" :title="person" :alt="person">
            </template>
          </template>
          <span v-else class="text-placeholder text-[13px]">&mdash;</span>
        </div>

        <!-- Col 7: Actions -->
        <div class="card-actions flex items-center gap-0.5 shrink-0 w-[100px] opacity-0 transition-opacity duration-150 max-md:opacity-100 max-md:order-[21] max-md:w-auto" @click.prevent.stop>
          <button v-if="user && user.isAdmin" type="button" class="flex items-center justify-center w-8 h-8 rounded-lg text-muted bg-transparent border-0 cursor-pointer transition-[background,color] duration-150 hover:bg-[#f0eef5] hover:text-primary" title="Edit" @click.prevent="openEditModal(applicant)">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
          </button>
          <a v-if="applicant.linkedin" :href="applicant.linkedin" target="_blank" class="flex items-center justify-center w-8 h-8 rounded-lg text-muted no-underline transition-[background,color] duration-150 hover:bg-[#f0eef5] hover:text-primary hover:no-underline" title="LinkedIn" @click.stop>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
          </a>
          <template v-if="user && user.isAdmin && applicant.isActive">
            <template v-if="confirmArchiveId === applicant.id">
              <button type="button" class="flex items-center justify-center px-2.5 h-8 rounded-lg text-xs font-semibold text-[#e53935] bg-transparent border-0 cursor-pointer transition-[background,color] duration-150 hover:bg-[#fef2f2]" @click.prevent.stop="archiveApplicant(applicant)">Archive?</button>
              <button type="button" class="flex items-center justify-center px-2 h-8 rounded-lg text-xs font-medium text-muted bg-transparent border-0 cursor-pointer transition-[background,color] duration-150 hover:bg-canvas hover:text-ink" @click.prevent.stop="confirmArchiveId = null">Cancel</button>
            </template>
            <button v-else type="button"
              class="flex items-center justify-center w-8 h-8 rounded-lg text-muted bg-transparent border-0 cursor-pointer transition-[background,color] duration-150 hover:bg-[#fef2f2] hover:text-[#e53935]"
              title="Archive"
              @click.prevent.stop="archiveApplicant(applicant)">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
            </button>
          </template>
          <button v-if="user && user.isAdmin && !applicant.isActive" type="button"
            class="flex items-center justify-center px-3 h-8 rounded-lg text-xs font-medium text-[#2d7a0e] bg-transparent border-0 cursor-pointer transition-[background,color] duration-150 hover:bg-[#f0f9eb] hover:text-[#2d7a0e]"
            title="Restore"
            @click.prevent.stop="restoreApplicant(applicant)">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1"><polyline points="1 4 1 10 7 10"></polyline><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path></svg>
            Restore
          </button>
        </div>

      </router-link>
    </div>

    <applicant-add ref="modal" :item="editApplicant" @update="update"></applicant-add>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import ApplicantAdd from './Includes/ApplicantAddModal'
import { PHOTOS } from '@/services/photos'
import { getCompletedPercentage, getAssignedToText } from '@/services/applicant-helpers'
import SkeletonApplicantList from '../UIComponents/SkeletonApplicantList'
import BaseSelect from '../UIComponents/BaseSelect'

export default {
  name: 'applicants',
  components: {
    applicantAdd: ApplicantAdd,
    SkeletonApplicantList,
    BaseSelect
  },
  data () {
    return {
      editApplicant: null,
      isLoading: false,
      searchQuery: '',
      debouncedSearchQuery: '',
      _searchTimer: null,
      activeTab: 'all',
      sortField: 'name',
      sortDirection: 'asc',
      selectedBody: '',
      confirmArchiveId: null,
      tabs: [
        { key: 'all', label: 'All' },
        { key: 'talent', label: 'Talent' },
        { key: 'promise', label: 'Promise' },
        { key: 'inactive', label: 'Inactive', adminOnly: true }
      ]
    }
  },
  watch: {
    searchQuery (val) {
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        this.debouncedSearchQuery = val
      }, 200)
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      applicants: state => state.applicants.applicants,
      applicantTypeOptions: state => state.applicants.applicantTypeOptions,
      applicationTypes: state => state.auth.applicationTypes,
      bodies: state => state.managers.bodies,
      managers: state => state.managers.managers
    }),
    visibleTabs () {
      if (this.user && this.user.isAdmin) return this.tabs
      return this.tabs.filter(t => !t.adminOnly)
    },
    filteredApplicants () {
      if (!this.user || (!this.user.isAdmin && !this.user.isManager)) {
        return []
      }
      let list = [...this.applicants]
      if (this.user.isManager && !this.user.isAdmin) {
        list = list.filter(applicant => {
          return applicant.availableTo && applicant.availableTo.includes(this.user.firstName.toLowerCase())
        })
      }
      list.sort((a, b) => b.isActive - a.isActive)
      return list
    },
    filteredAndSortedApplicants () {
      if (this.activeTab === 'all') {
        return this.activeApplicants
      } else if (this.activeTab === 'inactive') {
        return this.filteredApplicants.filter(applicant => {
          return !applicant.isActive
        })
      } else if (this.activeTab === 'promise') {
        return this.filteredApplicants.filter(applicant => {
          return applicant.isActive && applicant.type === 'promise'
        })
      } else if (this.activeTab === 'talent') {
        return this.filteredApplicants.filter(applicant => {
          return applicant.isActive && applicant.type === 'talent'
        })
      }
    },
    bodyFilteredApplicants () {
      const base = this.filteredAndSortedApplicants || []
      if (!this.selectedBody) {
        return base
      }
      return base.filter(applicant => {
        return applicant.endorsingBody === this.selectedBody
      })
    },
    sortedApplicants () {
      const list = [...this.bodyFilteredApplicants]
      const dir = this.sortDirection === 'asc' ? 1 : -1
      list.sort((a, b) => {
        switch (this.sortField) {
          case 'name': {
            const nameA = this.applicantName(a).toLowerCase()
            const nameB = this.applicantName(b).toLowerCase()
            return dir * nameA.localeCompare(nameB)
          }
          case 'type': {
            const typeA = (a.type || '').toLowerCase()
            const typeB = (b.type || '').toLowerCase()
            return dir * typeA.localeCompare(typeB)
          }
          case 'progress': {
            return dir * (this.getCompletedPercentage(a) - this.getCompletedPercentage(b))
          }
          case 'deadline': {
            const dA = a.deadline ? new Date(a.deadline).getTime() : 0
            const dB = b.deadline ? new Date(b.deadline).getTime() : 0
            return dir * (dA - dB)
          }
          default:
            return 0
        }
      })
      return list
    },
    searchedApplicants () {
      if (!this.debouncedSearchQuery.trim()) {
        return this.sortedApplicants
      }
      const query = this.debouncedSearchQuery.toLowerCase().trim()
      return this.sortedApplicants.filter(applicant => {
        const name = this.applicantName(applicant).toLowerCase()
        const body = this.getEndorsingBody(applicant.endorsingBody).toLowerCase()
        return name.includes(query) || body.includes(query)
      })
    },
    activeApplicants () {
      return this.filteredApplicants.filter(applicant => {
        return applicant.isActive
      })
    },
    quickStats () {
      const applicants = this.searchedApplicants || []
      let overdue = 0
      let warning = 0
      let completed = 0
      applicants.forEach(applicant => {
        if (this.getCompletedPercentage(applicant) === 100) {
          completed++
        }
        const urgency = this.getDeadlineUrgency(applicant)
        if (urgency === 'danger') overdue++
        else if (urgency === 'warning') warning++
      })
      const stats = []
      if (overdue > 0) stats.push({ label: 'due this week', count: overdue, type: 'danger' })
      if (warning > 0) stats.push({ label: 'nearing deadline', count: warning, type: 'warning' })
      if (completed > 0) stats.push({ label: 'completed', count: completed, type: 'success' })
      stats.push({ label: 'total', count: applicants.length, type: 'neutral' })
      return stats
    }
  },
  methods: {
    getDeadlineUrgency (applicant) {
      if (!applicant.deadline || !applicant.isActive || this.applicationCompleted(applicant)) {
        return null
      }
      const deadline = new Date(applicant.deadline)
      const today = new Date()
      const diff = deadline - today
      const days = diff / (1000 * 60 * 60 * 24)
      if (days < 7) return 'danger'
      if (days < 30) return 'warning'
      return 'safe'
    },
    toggleSort (field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDirection = 'asc'
      }
    },
    getTabCount (key) {
      if (key === 'all') return this.activeApplicants.length
      if (key === 'inactive') return this.filteredApplicants.filter(a => !a.isActive).length
      if (key === 'talent') return this.filteredApplicants.filter(a => a.isActive && a.type === 'talent').length
      if (key === 'promise') return this.filteredApplicants.filter(a => a.isActive && a.type === 'promise').length
      return 0
    },
    getPersonPhoto (name) {
      return PHOTOS[name.toLowerCase()] || null
    },
    getPhotos (applicant) {
      let people = []
      if (applicant.requirements) {
        applicant.requirements.forEach(requirement => {
          if (requirement.assignedTo && (requirement.status === 'toCheck' || requirement.status === 'created' || requirement.status === 'inProgress')) {
            requirement.assignedTo.forEach(manager => {
              if (people.indexOf(manager) === -1) {
                people.push(manager)
              }
            })
          }
        })
      }
      return people
    },
    getAssignedTo (requirement) {
      return getAssignedToText(requirement, this.managers)
    },
    applicationCompleted (applicant) {
      if (this.getCompletedPercentage(applicant) === 100) {
        return true
      } else {
        return false
      }
    },
    getCompletedPercentage (applicant) {
      return getCompletedPercentage(applicant)
    },
    getCompletedCount (applicant) {
      let completed = 0
      if (applicant.requirements) {
        applicant.requirements.forEach(requirement => {
          if (requirement.status && (requirement.status === 'complete' || requirement.status === 'submitted' || requirement.status === 'sentToApplicant')) {
            completed++
          }
        })
      }
      return completed
    },
    applicantName (applicant) {
      return applicant.firstName + ' ' + applicant.lastName
    },
    getEndorsingType (type) {
      const item = this.applicationTypes.find((item) => {
        return item.value === type
      })
      return item ? item.text : ''
    },
    getEndorsingBody (type) {
      const item = this.bodies.find((item) => {
        return item.slug === type
      })
      return item ? item.title : ''
    },
    openEditModal (applicant) {
      this.editApplicant = applicant || null
      this.$refs.modal.showModal()
    },
    update () {
      this.$store.dispatch('GET_APPLICANTS')
      this.$refs.modal.hideModal()
    },
    isExpired (applicant) {
      const deadline = new Date(applicant.deadline)
      const today = new Date()
      const diff = deadline - today
      const days = diff / (1000 * 60 * 60 * 24)
      return days < 7
    },
    getKeyDatesCount (applicant) {
      if (!applicant.keyDates || !applicant.keyDates.length) return 0
      return applicant.keyDates.filter(d => !d.completed).length
    },
    getOverdueKeyDates (applicant) {
      if (!applicant.keyDates || !applicant.keyDates.length) return 0
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return applicant.keyDates.filter(d => !d.completed && new Date(d.date) < today).length
    },
    getUpcomingKeyDates (applicant) {
      if (!applicant.keyDates || !applicant.keyDates.length) return 0
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const twoWeeks = new Date(today)
      twoWeeks.setDate(twoWeeks.getDate() + 14)
      return applicant.keyDates.filter(d => {
        if (d.completed) return false
        const date = new Date(d.date)
        return date >= today && date <= twoWeeks
      }).length
    },
    restoreApplicant (applicant) {
      const data = { ...applicant, isActive: true }
      this.$store.dispatch('UPDATE_APPLICANT', data).then(() => {
        this.$store.dispatch('GET_APPLICANTS')
        this.$store.dispatch('SHOW_TOAST', { message: 'Applicant restored' })
        const userName = this.user ? (this.user.firstName || this.user.username) : 'Unknown'
        this.$store.dispatch('CREATE_ACTIVITY', {
          applicantId: applicant.id,
          type: 'status',
          message: 'restored the applicant',
          details: null,
          user: userName
        }).catch(() => {})
      }).catch(() => {
        this.$store.dispatch('SHOW_TOAST', { message: 'Failed to restore applicant', type: 'error' })
      })
    },
    archiveApplicant (applicant) {
      if (this.confirmArchiveId === applicant.id) {
        this.confirmArchiveId = null
        const data = { ...applicant, isActive: false }
        this.$store.dispatch('UPDATE_APPLICANT', data).then(() => {
          this.$store.dispatch('GET_APPLICANTS')
          this.$store.dispatch('SHOW_TOAST', { message: 'Applicant archived' })
          const userName = this.user ? (this.user.firstName || this.user.username) : 'Unknown'
          this.$store.dispatch('CREATE_ACTIVITY', {
            applicantId: applicant.id,
            type: 'status',
            message: 'archived the applicant',
            details: null,
            user: userName
          }).catch(() => {})
        }).catch(() => {
          this.$store.dispatch('SHOW_TOAST', { message: 'Failed to archive applicant', type: 'error' })
        })
      } else {
        this.confirmArchiveId = applicant.id
      }
    }
  },
  mounted () {
    this._resetConfirmArchive = () => { this.confirmArchiveId = null }
    document.addEventListener('click', this._resetConfirmArchive)
    this._handleKeydown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        if (document.body.classList.contains('modal-open')) return
        e.preventDefault()
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus()
        }
      }
    }
    document.addEventListener('keydown', this._handleKeydown)
  },
  beforeDestroy () {
    if (this._resetConfirmArchive) {
      document.removeEventListener('click', this._resetConfirmArchive)
    }
    if (this._handleKeydown) {
      document.removeEventListener('keydown', this._handleKeydown)
    }
    clearTimeout(this._searchTimer)
  },
  created () {
    // Apply sort/filter from query params (e.g. from overview stat links)
    const query = this.$route.query
    if (query.sort && ['name', 'type', 'progress', 'deadline'].includes(query.sort)) {
      this.sortField = query.sort
    }
    if (query.dir && ['asc', 'desc'].includes(query.dir)) {
      this.sortDirection = query.dir
    }

    this.isLoading = true
    this.$store.dispatch('GET_APPLICANTS').then(() => {
      this.isLoading = false
    }).catch((error) => {
      this.isLoading = false
      console.error('Failed to load applicants:', error)
      if (error.code === 'permission-denied') {
        this.$router.push({ name: 'login' })
      }
    })

    this.$store.dispatch('GET_BODIES').catch((error) => {
      console.error('Failed to load bodies:', error)
    })

    if ((this.user && !this.user.isAdmin) && (this.user && !this.user.isManager)) {
      this.$router.push({ name: 'components' })
    }
  }
}
</script>

<style scoped>
/* Active tab count color change */
.tab-btn.bg-white .tab-count {
  color: #7c5bea;
}

/* Search box focus-within */
.search-box:focus-within {
  border-color: #7c5bea;
  background: #fff;
}

.search-box:focus-within svg {
  color: #7c5bea;
}

/* Parent-hover-child: show actions on card hover */
.group:hover .card-actions {
  opacity: 1;
}

/* Person avatar stacking — negative margin for overlap */
.group img + img {
  margin-left: -6px;
}

/* Sort header: show arrow hint on hover */
.sort-header:hover .sort-chevron {
  opacity: 0.4 !important;
}

.sort-header:hover .sort-chevron.text-primary {
  opacity: 1 !important;
}

/* Complete card: check badge border matches green background */
.bg-\[\#f8fbf4\] .border-white {
  border-color: #f8fbf4;
}
</style>
